/**
 * Componente de seleção de data para agendamento público - Design Premium
 * 
 * Exibe um calendário mensal para o visitante selecionar a data desejada.
 * Respeita o max_advance_days e não permite datas passadas.
 * 
 * RELEASE: 2026-02-14
 * VERSION: 2.0.0 - Redesign premium com hover refinado e dot indicator
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isAfter,
  isBefore,
  addDays,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { PublicEventType } from '@/hooks/usePublicScheduling';
import { useBlockedDates, useUnavailableWeekdays } from '@/hooks/usePublicScheduling';

interface PublicSchedulingDatePickerProps {
  eventType: PublicEventType;
  onDateSelect: (date: string) => void;
}

/** Nomes abreviados dos dias da semana */
const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function PublicSchedulingDatePicker({ eventType, onDateSelect }: PublicSchedulingDatePickerProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventColor = eventType.color || '#3B82F6';
  const maxDate = addDays(today, eventType.max_advance_days);
  const [currentMonth, setCurrentMonth] = useState(today);

  // (v1.6.0) Buscar datas bloqueadas por exceção (date overrides)
  const { data: blockedDates = [] } = useBlockedDates(eventType.id);

  // (v2.1.0) Buscar dias da semana indisponíveis (ex: sábado e domingo)
  const { data: unavailableWeekdays = [] } = useUnavailableWeekdays(eventType.id);

  // Gera os dias visíveis no calendário (inclui dias do mês anterior/seguinte)
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  /** Verifica se uma data pode ser selecionada */
  const isDateSelectable = (date: Date) => {
    if (isBefore(date, today)) return false;
    if (isAfter(date, maxDate)) return false;
    // (v2.1.0) Verificar se o dia da semana está indisponível na configuração
    if (unavailableWeekdays.includes(date.getDay())) return false;
    // (v1.6.0) Verificar se a data está bloqueada por exceção
    const dateStr = format(date, 'yyyy-MM-dd');
    if (blockedDates.includes(dateStr)) return false;
    return true;
  };

  /** Navega para o mês anterior */
  const handlePrevMonth = () => {
    const prev = subMonths(currentMonth, 1);
    if (endOfMonth(prev) >= today) {
      setCurrentMonth(prev);
    }
  };

  /** Navega para o próximo mês */
  const handleNextMonth = () => {
    const next = addMonths(currentMonth, 1);
    if (startOfMonth(next) <= maxDate) {
      setCurrentMonth(next);
    }
  };

  /** Seleciona uma data */
  const handleDayClick = (date: Date) => {
    if (!isDateSelectable(date)) return;
    const formatted = format(date, 'yyyy-MM-dd');
    onDateSelect(formatted);
  };

  return (
    <div className="p-6 sm:p-8">
      {/* Título da etapa */}
      <h2 className="text-lg font-semibold text-foreground mb-1">
        Selecione uma data
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        Escolha o melhor dia para o seu agendamento
      </p>

      {/* Navegação do mês - estilo premium */}
      <div className="flex items-center justify-between mb-5">
        <Button
          variant="ghost"
          size="icon"
          onClick={handlePrevMonth}
          className="h-9 w-9 rounded-full hover:bg-muted"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-semibold text-foreground capitalize tracking-wide">
          {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleNextMonth}
          className="h-9 w-9 rounded-full hover:bg-muted"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Cabeçalho dos dias da semana */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEK_DAYS.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-semibold text-muted-foreground/70 py-2 uppercase tracking-wider"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Grade de dias - estilo premium */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isToday = isSameDay(day, today);
          const selectable = isDateSelectable(day);

          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={!selectable || !isCurrentMonth}
              onClick={() => handleDayClick(day)}
              className={cn(
                "relative h-11 w-full rounded-xl text-sm font-medium transition-all duration-200",
                // Dias fora do mês
                !isCurrentMonth && "text-muted-foreground/20 cursor-default",
                // Dias não selecionáveis do mês atual
                isCurrentMonth && !selectable && "text-muted-foreground/30 cursor-not-allowed",
                // Dias selecionáveis - hover premium
                isCurrentMonth && selectable && "text-foreground cursor-pointer hover:scale-105 hover:shadow-md",
                // Hoje - ring sutil
                isToday && "ring-1 ring-primary/40 font-bold",
              )}
              style={
                isCurrentMonth && selectable
                  ? { '--hover-bg': `${eventColor}12` } as React.CSSProperties
                  : undefined
              }
              onMouseEnter={(e) => {
                if (isCurrentMonth && selectable) {
                  e.currentTarget.style.backgroundColor = `${eventColor}12`;
                }
              }}
              onMouseLeave={(e) => {
                if (isCurrentMonth && selectable) {
                  e.currentTarget.style.backgroundColor = '';
                }
              }}
            >
              {format(day, 'd')}
              {/* Dot indicator para dias selecionáveis */}
              {isCurrentMonth && selectable && (
                <span
                  className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full"
                  style={{ backgroundColor: eventColor }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
